import { useState } from "react";
import { Car } from "lucide-react";
import { useApi } from "@/hooks/useApi";
import { formatCurrency, formatDate } from "@/lib/utils";
import { dealerService } from "@/services/DealerService";
import { DealerStockFeedItem } from "@/types/dealer";
import { DealerTabSection, DealerTabState } from "./DealerTabState";
import { RegistrationPlate } from "./RegistrationPlate";

const isSafeImageUrl = (url: string | null): url is string => !!url && /^https?:\/\//i.test(url.trim());

// const displayValue = (value: string | null) => (value && value.trim() ? value : "-");

const displayDate = (value: string | null) => (value ? formatDate(value) : "-");

function StockFeedImage({ item }: { item: DealerStockFeedItem }) {
  const [hasError, setHasError] = useState(false);
  const alt = [item.make, item.model].filter(Boolean).join(" ") || "Vehicle";

  const handleImageError = () => setHasError(true);

  if (!isSafeImageUrl(item.pictureUrl) || hasError) {
    return (
      <div
        className="bg-muted text-muted-foreground flex h-12 w-16 items-center justify-center rounded"
        data-testid="stock-feed-image-placeholder"
      >
        <Car className="h-5 w-5" aria-hidden="true" />
      </div>
    );
  }

  return (
    <img
      src={item.pictureUrl.trim()}
      alt={alt}
      loading="lazy"
      className="h-12 w-16 rounded object-cover"
      onError={handleImageError}
    />
  );
}

export function StockFeedTab({ dealerId }: { dealerId: number }) {
  const { data, loading, error } = useApi((signal) => dealerService.getDealerStockFeed(dealerId, signal), [dealerId]);

  return (
    <DealerTabState
      data={data}
      loading={loading}
      error={error}
      label="stock feed"
      emptyMessage="No stock feed vehicles found for this dealer."
    >
      {(items) => (
        <DealerTabSection title="Stock feed" count={items.length}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-max text-sm">
              <thead>
                <tr className="bg-muted/40 border-b text-left">
                  <th className="p-3 font-medium">Image</th>
                  <th className="p-3 font-medium">Vehicle</th>
                  <th className="p-3 font-medium">Registration</th>
                  <th className="p-3 text-right font-medium">Price</th>
                  <th className="p-3 font-medium">Added</th>
                  <th className="p-3 font-medium">Updated</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.vehicleId} className="border-b last:border-b-0">
                    <td className="p-3">
                      <StockFeedImage item={item} />
                    </td>
                    <td className="p-3">
                      <div className="font-semibold">
                        {[item.make, item.model].filter(Boolean).join(" ") || "-"}
                      </div>
                      {item.variant && <div className="text-muted-foreground text-xs">{item.variant}</div>}
                    </td>
                    <td className="p-3">{RegistrationPlate({ reg: item.registration })}</td>
                    <td className="p-3 text-right">{item.price === null ? "-" : formatCurrency(item.price)}</td>
                    <td className="p-3">{displayDate(item.createdOn)}</td>
                    <td className="p-3">{displayDate(item.modifiedOn)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DealerTabSection>
      )}
    </DealerTabState>
  );
}
